import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import { SEO } from "@/components/common/SEO";
import { imageFor } from "@/lib/imagery";
import type { RouteKey } from "@/lib/routes";

const SECTORS: RouteKey[] = [
  "commodities.grains",
  "commodities.food",
  "commodities.petrochemical",
];

// Homepage capabilities (5). Labels/ledes are homepage copy; each links to an existing route.
const CAPABILITIES: Array<{ label: string; lede: string; to: RouteKey }> = [
  { label: "International Trade", lede: "Cross-border trade of physical commodities under standard international terms.", to: "services.trade" },
  { label: "Commodity Trading", lede: "Buying and selling physical commodities across origins and destinations.", to: "services.index" },
  { label: "Industrial Supply", lede: "Structured, recurring supply of raw and processed materials to industrial buyers.", to: "services.supply" },
  { label: "Global Sourcing", lede: "Origination and qualification of suppliers across multiple origins.", to: "services.sourcing" },
  { label: "Logistics & Freight", lede: "Multimodal movement by sea, rail and road through Black Sea ports and the Europe–Asia corridor.", to: "services.index" },
];

const COMPLIANCE_LINKS: RouteKey[] = [
  "compliance.corporate",
  "compliance.kyc",
  "compliance.antifraud",
];

export default function Home() {
  const { t, path, locale } = useT();
  const heroImg = imageFor("home");

  // Country/positioning tokens — factual attributes, no invented content
  const positionTokens = ["Registered in Georgia", "Black Sea gateway", "Europe–Asia corridor", "GAFTA · FOSFA · Incoterms 2020"];

  const complianceLede = {
    "compliance.corporate": "Registered company data and official references.",
    "compliance.kyc":       "Counterparty verification through official sources.",
    "compliance.antifraud": "Official warning against impersonation and fraud.",
  } as Record<RouteKey, string>;

  return (
    <>
      <SEO routeKey="home" />

      {/* ── HERO ── deeper, calmer, more institutional ── */}
      <section className="relative bg-ink text-bone overflow-hidden" data-testid="home-hero">
        {heroImg && (
          <div className="absolute inset-0">
            <img
              src={heroImg.url}
              srcSet={heroImg.srcSet}
              sizes="100vw"
              alt=""
              aria-hidden
              className="w-full h-full object-cover opacity-35"
              loading="eager"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/45" />
          </div>
        )}
        <div className="relative container-bnm pt-32 md:pt-40 pb-28 md:pb-40">
          <p className="eyebrow !text-clay-400 mb-8">{t.home.heroEyebrow}</p>
          <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[0.98] tracking-tight max-w-[17ch] text-bone">
            {t.home.heroTitle}
          </h1>
          <p className="mt-10 md:mt-12 text-lg md:text-xl text-bone/70 max-w-prose-tight leading-relaxed">
            {t.home.heroSub}
          </p>
          <div className="mt-10 md:mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <Link
              to={path("contact")}
              className="inline-flex items-center gap-3 bg-clay text-bone px-8 py-4 font-medium hover:bg-clay-600 transition-colors group"
              data-testid="home-hero-cta"
            >
              {t.home.heroCta}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to={path("services.index")}
              className="inline-flex items-center gap-3 text-bone border-b border-bone/50 pb-1 hover:text-clay-400 hover:border-clay-400 transition-colors"
              data-testid="home-hero-cta-secondary"
            >
              {t.home.heroCtaSecondary}
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>

        {/* Positioning ribbon — pinned to bottom of hero, minimal */}
        <div className="relative border-t border-bone/10">
          <div className="container-bnm py-6 md:py-8 flex flex-wrap items-center gap-x-10 gap-y-3">
            <span className="font-mono text-[10px] uppercase tracking-eyebrow text-bone/50">
              {"Registered"}
            </span>
            {positionTokens.map((tok) => (
              <span key={tok} className="font-mono text-[11px] uppercase tracking-[0.16em] text-bone">
                {tok}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATEMENT ── quieter, more institutional ── */}
      <section className="border-b border-bone-500" data-testid="home-statement">
        <div className="container-bnm py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-3">
            <p className="eyebrow">{t.home.statementEyebrow}</p>
          </div>
          <div className="lg:col-span-9">
            <p className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-ink max-w-[28ch]">
              {t.home.statement}
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTORS ── magazine-grade image cards with real sector imagery ── */}
      <section className="border-b border-bone-500" data-testid="home-sectors">
        <div className="container-bnm py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20">
            <div className="lg:col-span-3">
              <p className="eyebrow">{t.home.sectorsEyebrow}</p>
            </div>
            <div className="lg:col-span-9">
              <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-[22ch]">
                {t.home.sectorsTitle}
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {SECTORS.map((key, idx) => {
              const img = imageFor(key);
              const meta = t.routes[key] as { title: string; lede?: string };
              return (
                <Link
                  key={key}
                  to={path(key)}
                  className="group flex flex-col"
                  data-testid={`home-sector-${key.split(".").pop()}`}
                >
                  {img && (
                    <div className="aspect-[4/5] overflow-hidden bg-ink/5 mb-6">
                      <img
                        src={img.url}
                        srcSet={img.srcSet}
                        sizes="(min-width: 768px) 33vw, 100vw"
                        alt={img.alt}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    </div>
                  )}
                  <div className="flex items-baseline justify-between mb-3">
                    <p className="font-mono text-[10px] uppercase tracking-eyebrow text-slate2">
                      {"Sector"} {String(idx + 1).padStart(2, "0")}
                    </p>
                    <ArrowUpRight size={18} className="text-ink group-hover:text-clay group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" aria-hidden />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-ink group-hover:text-clay transition-colors">
                    {meta.title}
                  </h3>
                  {meta.lede && (
                    <p className="mt-3 text-slate2 leading-relaxed text-sm">
                      {meta.lede}
                    </p>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── typographic index list ── */}
      <section className="border-b border-bone-500" data-testid="home-services">
        <div className="container-bnm py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20">
            <div className="lg:col-span-3">
              <p className="eyebrow">{t.home.servicesEyebrow}</p>
            </div>
            <div className="lg:col-span-9">
              <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-[22ch]">
                {t.home.servicesTitle}
              </h2>
            </div>
          </div>

          <ul className="border-t border-bone-500">
            {CAPABILITIES.map((item, idx) => {
              return (
                <li key={item.label} className="border-b border-bone-500 group">
                  <Link
                    to={path(item.to)}
                    className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-9 md:py-11 items-baseline -mx-4 md:-mx-6 px-4 md:px-6 hover:bg-bone-300/40 transition-colors"
                    data-testid={`home-capability-${idx + 1}`}
                  >
                    <div className="md:col-span-1 font-mono text-[11px] tracking-eyebrow text-slate2 uppercase">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <div className="md:col-span-4">
                      <h3 className="font-display font-light text-2xl md:text-4xl text-ink group-hover:text-clay transition-colors">
                        {item.label}
                      </h3>
                    </div>
                    <div className="md:col-span-6">
                      <p className="text-slate2 leading-relaxed">
                        {item.lede}
                      </p>
                    </div>
                    <div className="md:col-span-1 flex md:justify-end">
                      <ArrowUpRight size={18} className="text-ink group-hover:text-clay group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" aria-hidden />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ── COMPLIANCE / TRUST ── denser signal, now with real lede text per card ── */}
      <section className="border-b border-bone-500 bg-bone-300/50" data-testid="home-trust">
        <div className="container-bnm py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-6">{t.home.trustEyebrow}</p>
            <h2 className="font-display font-light text-4xl md:text-5xl leading-[1.08] tracking-tight text-ink max-w-[20ch]">
              {t.home.trustTitle}
            </h2>
            <p className="mt-6 text-slate2 leading-relaxed max-w-prose-tight">
              {t.home.trustBody}
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {COMPLIANCE_LINKS.map((key, idx) => (
              <Link
                key={key}
                to={path(key)}
                className="group border border-bone-500 hover:border-ink bg-bone p-6 flex flex-col justify-between h-full transition-colors gap-6"
                data-testid={`home-compliance-${key.split(".").pop()}`}
              >
                <span className="font-mono text-[10px] uppercase tracking-eyebrow text-slate2">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-display text-xl md:text-2xl text-ink group-hover:text-clay transition-colors leading-snug">
                    {t.routes[key].label}
                  </h3>
                  <p className="mt-3 text-sm text-slate2 leading-relaxed">
                    {complianceLede[key]}
                  </p>
                </div>
                <ArrowUpRight size={16} className="text-ink group-hover:text-clay" aria-hidden />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-ink text-bone" data-testid="home-cta">
        <div className="container-bnm py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow !text-bone/60 mb-6">{t.home.ctaEyebrow}</p>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-bone max-w-[18ch]">
              {t.home.ctaTitle}
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to={path("contact")}
              className="inline-flex items-center gap-3 bg-clay text-bone px-8 py-4 font-medium hover:bg-clay-600 transition-colors group"
              data-testid="home-cta-btn"
            >
              {t.home.ctaAction}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
